import { FirebaseError } from "firebase/app";

export type UserFacingError =
  | { kind: "network" }
  | { kind: "auth"; code?: string }
  | { kind: "permissions" }
  | { kind: "validation"; detail: string }
  | { kind: "server" }
  | { kind: "generic" };

const NETWORK_CODES: ReadonlySet<string> = new Set([
  "auth/network-request-failed",
  "functions/unavailable",
  "functions/deadline-exceeded",
  "unavailable",
  "deadline-exceeded",
]);

const PERMISSION_CODES: ReadonlySet<string> = new Set([
  "permission-denied",
  "firestore/permission-denied",
  "storage/unauthorized",
  "functions/permission-denied",
  "functions/unauthenticated",
  "unauthenticated",
]);

const SERVER_CODES: ReadonlySet<string> = new Set([
  "functions/internal",
  "internal",
  "data-loss",
  "functions/data-loss",
  "functions/unknown",
  "unknown",
]);

const AUTH_USER_FIXABLE: Record<string, string> = {
  "auth/email-already-in-use": "That email is already in use.",
  "auth/invalid-email": "That email address doesn't look right.",
  "auth/weak-password": "Pick a stronger password (at least 6 characters).",
  "auth/wrong-password": "That password isn't right. Try again.",
  "auth/invalid-credential":
    "Hmm, that doesn't look right. Check your email and password and try again.",
  "auth/user-not-found": "We couldn't find an account with that email.",
  "auth/user-disabled": "This account has been disabled.",
  "auth/too-many-requests": "Too many attempts. Wait a moment and try again.",
  "auth/popup-closed-by-user": "Sign-in was cancelled.",
  "auth/popup-blocked": "Your browser blocked the sign-in popup. Allow popups and try again.",
  "auth/cancelled-popup-request": "Sign-in was cancelled.",
  "auth/account-exists-with-different-credential":
    "An account with that email already exists. Try a different sign-in method.",
};

export function userFacingError(err: unknown): UserFacingError {
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return { kind: "network" };
  }

  if (err instanceof FirebaseError) {
    const code = err.code;
    if (NETWORK_CODES.has(code)) return { kind: "network" };
    if (PERMISSION_CODES.has(code)) return { kind: "permissions" };
    if (SERVER_CODES.has(code)) return { kind: "server" };
    if (code in AUTH_USER_FIXABLE) {
      return { kind: "validation", detail: AUTH_USER_FIXABLE[code] };
    }
    if (code.startsWith("auth/")) {
      return { kind: "auth", code };
    }
    return { kind: "generic" };
  }

  if (err instanceof TypeError && /fetch|network/i.test(err.message)) {
    return { kind: "network" };
  }

  return { kind: "generic" };
}

export function userFacingMessage(err: unknown, fallback?: string): string {
  const mapped = userFacingError(err);
  switch (mapped.kind) {
    case "network":
      return "You're offline. Check your connection and try again.";
    case "auth":
      return "Your session has expired. Please sign in again.";
    case "permissions":
      return "You don't have permission to do that.";
    case "validation":
      return mapped.detail;
    case "server":
      return "We hit a snag on our end. Please try again.";
    case "generic":
      return fallback ?? "Something went wrong. Please try again.";
  }
}
